import React, { useEffect, useState } from 'react';
import {
    VictoryAxis,
    VictoryChart,
    VictoryLabel,
    VictoryLegend,
    VictoryLine,
    VictoryTheme,
    VictoryTooltip,
    VictoryVoronoiContainer,
} from 'victory';

const DAYSBACK = 2;
const DAYS = 10; // increase to 700 once sunrise endpoint supports it

function parseHour(timeStr) {
    if (!timeStr) return null;
    const [time, period] = timeStr.split(' ');
    let [h, m] = time.split(':').map(Number);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h + m / 60;
}

function monthLabel(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleString('en-GB', { month: 'short', year: '2-digit' });
}

function dailyAvg(records, keyFn, valueFn) {
    const map = {};
    records.forEach(r => {
        const key = keyFn(r);
        if (!map[key]) map[key] = [];
        const val = valueFn(r);
        if (val !== null && val !== undefined) map[key].push(val);
    });
    return Object.entries(map)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, vals]) => ({
            date,
            avg: parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)),
        }));
}

const SolarSeasonalGraph = () => {
    const [solarDaily, setSolarDaily] = useState([]);
    const [clearDaily, setClearDaily] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const BASE = 'https://api.shortdarkapps.com/v1/sunny';

        Promise.all([
            fetch(`${BASE}/carbon/region/5/daysback/${DAYSBACK}/days/${DAYS}/type/solar`).then(r => r.json()),
            fetch(`${BASE}/weather/daysback/${DAYSBACK}/days/${DAYS}`).then(r => r.json()),
            fetch(`${BASE}/sunrise/daysback/${DAYSBACK}/days/${DAYS}`).then(r => r.json()),
        ])
            .then(([carbonRaw, weatherRaw, sunriseRaw]) => {
                // Build sunrise/sunset lookup by date
                const sunMap = {};
                (sunriseRaw.results || []).forEach(r => {
                    sunMap[r.date] = {
                        rise: parseHour(r.sunrise),
                        set: parseHour(r.sunset),
                    };
                });

                // Daily average solar %
                const solar = dailyAvg(
                    carbonRaw,
                    r => r.date_from.substring(0, 10),
                    r => r.solar,
                );

                // Daily average clear sky % filtered to daylight hours only
                const daylightWeather = weatherRaw.filter(r => {
                    const sun = sunMap[r.humandate];
                    const riseH = sun ? Math.floor(sun.rise) : 6;
                    const setH = sun ? Math.ceil(sun.set) : 21;
                    return r.humanhour >= riseH && r.humanhour <= setH;
                });

                const clear = dailyAvg(
                    daylightWeather,
                    r => r.humandate,
                    r => (r.clouds !== null ? 100 - r.clouds : null),
                );

                setSolarDaily(solar.map(({ date, avg }) => ({
                    x: monthLabel(date),
                    y: avg,
                    label: `${date}\nSolar: ${avg}%`,
                })));

                setClearDaily(clear.map(({ date, avg }) => ({
                    x: monthLabel(date),
                    y: avg,
                    label: `${date}\nClear sky: ${avg}%`,
                })));

                setLoading(false);
            })
            .catch(e => {
                setError(e.message);
                setLoading(false);
            });
    }, []);

    if (loading) return <p>Loading solar data...</p>;
    if (error) return <p>Error: {error}</p>;
    if (!solarDaily.length) return <p>No solar data</p>;

    return (
        <VictoryChart
            theme={VictoryTheme.material}
            width={600}
            height={350}
            padding={{ top: 50, bottom: 70, left: 60, right: 20 }}
            containerComponent={
                <VictoryVoronoiContainer
                    labels={({ datum }) => datum.label}
                    labelComponent={
                        <VictoryTooltip
                            style={{ fontSize: 7 }}
                            flyoutStyle={{ fill: 'white', stroke: '#ccc' }}
                        />
                    }
                />
            }
        >
            <VictoryLabel
                text="Solar Energy & Clear Sky % — Yorkshire (Region 5)"
                x={300}
                y={20}
                textAnchor="middle"
                style={{ fontSize: 10, fontWeight: 'bold' }}
            />

            {/* Solar % line */}
            <VictoryLine
                name="Solar Energy %"
                style={{
                    data: { stroke: '#e05a1e', strokeWidth: 2 },
                    parent: { border: '1px solid #ccc' },
                }}
                data={solarDaily}
            />

            {/* Clear sky % line */}
            <VictoryLine
                name="Clear Sky % (daylight hrs)"
                style={{
                    data: { stroke: '#1a7fc4', strokeWidth: 2 },
                    parent: { border: '1px solid #ccc' },
                }}
                data={clearDaily}
            />

            {/* Y axis */}
            <VictoryAxis
                dependentAxis
                tickFormat={t => `${t}%`}
                style={{
                    axis: { stroke: '#756f6a' },
                    axisLabel: { fontSize: 8, padding: 40 },
                    ticks: { stroke: 'grey', size: 5 },
                    tickLabels: { fontSize: 8, padding: 5 },
                }}
                label="Percentage"
            />

            {/* X axis */}
            <VictoryAxis
                fixLabelOverlap={true}
                style={{
                    axis: { stroke: '#756f6a' },
                    axisLabel: { fontSize: 8, padding: 40 },
                    ticks: { stroke: 'grey', size: 5 },
                    tickLabels: { angle: 45, fontSize: 8, padding: 5 },
                }}
                label="Date"
            />

            <VictoryLegend
                x={60}
                y={50}
                height={60}
                width={200}
                orientation="vertical"
                gutter={10}
                style={{
                    labels: { fill: 'navy', fontSize: 6 },
                    border: { fill: 'white', stroke: 'black', opacity: 0.8 },
                }}
                data={[
                    { name: 'Solar Energy %', symbol: { fill: '#e05a1e' } },
                    { name: 'Clear Sky % (daylight hrs)', symbol: { fill: '#1a7fc4' } },
                ]}
            />
        </VictoryChart>
    );
};

export default SolarSeasonalGraph;